import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import CartDrawer from '@/components/feature/CartDrawer';

const WHATSAPP_URL =
  'https://wa.me/5585982245030?text=Olá!%20Vim%20pelo%20site%20da%20Naturalle%20Sanduíches%20e%20gostaria%20de%20mais%20informações.';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { openCart, totalItems } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'Início', href: '#hero' },
    { label: 'Produtos', href: '#produtos' },
    { label: 'Qualidade', href: '#qualidade' },
    { label: 'Revenda', href: '#revenda' },
    { label: 'Contato', href: '#mapa' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
          scrolled
            ? 'bg-background-50/85 backdrop-blur-xl border-secondary-500/20'
            : 'bg-transparent border-transparent'
        }`}
      >
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-baseline gap-2.5 shrink-0">
              <span className="font-display text-[26px] md:text-3xl leading-none text-foreground-900">
                Naturalle
              </span>
              <span className="hidden sm:inline-block font-label text-[10px] md:text-[11px] font-medium tracking-[0.35em] uppercase text-secondary-500 mb-0.5">
                Sanduíches
              </span>
            </Link>

            {/* Desktop navigation */}
            <div className="hidden md:flex items-center gap-9">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="group relative font-label text-[13px] tracking-wide text-foreground-500 hover:text-foreground-900 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                  <span className="absolute -bottom-1.5 left-0 w-0 h-px bg-secondary-500 group-hover:w-full transition-all duration-500" />
                </button>
              ))}
            </div>

            {/* Desktop actions */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/cardapio"
                className="font-label text-[13px] tracking-wide px-5 py-2.5 rounded-full border border-secondary-500/30 text-foreground-700 hover:border-secondary-500 hover:text-secondary-400 transition-all duration-300 whitespace-nowrap"
              >
                Cardápio
              </Link>
              <button
                onClick={openCart}
                className="relative w-10 h-10 rounded-full border border-secondary-500/30 flex items-center justify-center text-foreground-600 hover:border-secondary-500 hover:text-secondary-400 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-shopping-bag-3-line text-lg" />
                {totalItems > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-accent-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {totalItems > 9 ? '9+' : totalItems}
                  </span>
                )}
              </button>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-label text-[13px] tracking-wide px-5 py-2.5 rounded-full bg-accent-500 text-white hover:bg-accent-400 transition-all duration-300 whitespace-nowrap"
              >
                Fale Conosco
              </a>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            >
              <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-xl`} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden absolute top-full left-0 right-0 bg-background-50/95 backdrop-blur-xl border-t border-secondary-500/20 transition-all duration-300 overflow-hidden ${
            menuOpen ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-6 py-6">
            <div className="space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="block w-full text-left font-label text-base text-foreground-600 py-2.5 hover:text-secondary-400 transition-colors cursor-pointer"
                >
                  {link.label}
                </button>
              ))}
            </div>
            <div className="pt-3 mt-2 flex flex-col gap-3 border-t border-background-200/60">
              <Link
                to="/cardapio"
                onClick={() => setMenuOpen(false)}
                className="font-label text-sm px-5 py-3 rounded-full border border-secondary-500/30 text-foreground-700 text-center hover:border-secondary-500 transition-all"
              >
                Cardápio
              </Link>
              <button
                onClick={() => { setMenuOpen(false); openCart(); }}
                className="font-label text-sm px-5 py-3 rounded-full border border-secondary-500/30 text-foreground-700 text-center hover:border-secondary-500 transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-shopping-bag-3-line" />
                Carrinho
                {totalItems > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent-500 text-white text-[10px] font-bold">
                    {totalItems > 9 ? '9+' : totalItems}
                  </span>
                )}
              </button>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-label text-sm px-5 py-3 rounded-full bg-accent-500 text-white text-center hover:bg-accent-400 transition-all"
              >
                Fale Conosco
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Cart Drawer */}
      <CartDrawer />
    </>
  );
};

export default Navbar;